import { motion } from "framer-motion";
import { Quote, Eye, Target } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PhotoFrame } from "@/components/Photo";
import { usePhotos } from "@/hooks/usePhotos";
import { QUOTES, VISION_MISSION, TERMS_EN, TERMS_HI } from "@/data/content";

export const FounderMessage = () => {
  const { first } = usePhotos("founder2");
  return (
  <section id="founder-message" data-testid="founder-message" className="py-24">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <SectionHeading eyebrow="A Signature Statement" title="Message From Founder" />
      <Reveal className="mt-12">
        <div className="grid items-start gap-8 lg:grid-cols-[1fr_360px]">
        <div className="relative [perspective:1400px]">
          <motion.div
            initial={{ rotateX: 10, y: 30, opacity: 0 }}
            whileInView={{ rotateX: 0, y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="glass relative rounded-[2rem] p-8 sm:p-12"
          >
            <span className="absolute -top-7 left-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-terra text-white shadow-[0_18px_40px_-14px_rgba(14,90,71,0.8)]"><Quote className="h-6 w-6" /></span>
            {QUOTES.message.split("\n\n").map((para, i) => (
              <p key={i} data-testid={`founder-message-para-${i}`} className="mt-5 font-display text-lg leading-[1.9] text-forest first:mt-2 sm:text-xl">{para}</p>
            ))}
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-terra">— Sunny Goyal · Founder, Goyal Academy</p>
          </motion.div>
        </div>
        <PhotoFrame photo={first} ratio="aspect-[4/5]" label="Founder — Sunny Goyal" alt="Sunny Goyal" className="lg:sticky lg:top-28" testid="founder-side-photo" />
        </div>
      </Reveal>
    </div>
  </section>
  );
};

const VmCard = ({ icon: Icon, label, text, bg, testid, delay }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 40, rotateY: -12 }}
    whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    style={{ transformStyle: "preserve-3d" }}
    className={`tilt-card relative overflow-hidden rounded-[2rem] border border-white/70 p-8 shadow-[0_30px_70px_-40px_rgba(28,35,51,0.35)] sm:p-10 ${bg}`}
  >
    <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/50 blur-2xl" />
    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-terra shadow-md"><Icon className="h-5 w-5" /></span>
    <p className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-terra">Our {label}</p>
    <h3 className="mt-2 font-display text-3xl font-semibold text-forest">{label}</h3>
    <p className="mt-4 text-sm leading-[1.9] text-charcoal/80 sm:text-base">{text}</p>
  </motion.div>
);

export const VisionMissionCards = () => (
  <section id="vision-mission" data-testid="vision-mission" className="py-24">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <SectionHeading big eyebrow="What Drives Us" title="Vision & Mission" />
      <div className="mt-12 grid gap-6 md:grid-cols-2 [perspective:1600px]">
        <VmCard icon={Eye} label="Vision" text={VISION_MISSION.vision} bg="bg-sage" testid="vision-card" delay={0} />
        <VmCard icon={Target} label="Mission" text={VISION_MISSION.mission} bg="bg-lavender" testid="mission-card" delay={0.12} />
      </div>
    </div>
  </section>
);

const TermsColumn = ({ title, items, testid, lang }) => (
  <div data-testid={testid} lang={lang} className="flex-1">
    <h3 className="font-display text-2xl font-semibold text-forest">{title}</h3>
    <ol className="mt-5 space-y-3">
      {items.map((t, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed text-charcoal/80">
          <span className="mt-0.5 shrink-0 font-display text-base font-semibold text-terra">{i + 1}.</span>
          <span>{t}</span>
        </li>
      ))}
    </ol>
  </div>
);

export const TermsSection = () => (
  <section id="terms" data-testid="terms-section" className="bg-ivory py-24">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <SectionHeading big eyebrow="Please Read Before Admission" title="Terms and Conditions" />
      <Reveal className="mt-12">
        <div className="glass flex flex-col gap-10 rounded-[2rem] p-6 sm:p-10 lg:flex-row lg:gap-0">
          <TermsColumn title="Terms and Conditions" items={TERMS_EN} testid="terms-english" lang="en" />
          <div className="my-2 h-px w-full bg-terra/30 lg:mx-10 lg:my-0 lg:h-auto lg:w-px lg:self-stretch" />
          <TermsColumn title="📋 नियम एवं शर्तें (Terms & Conditions)" items={TERMS_HI} testid="terms-hindi" lang="hi" />
        </div>
      </Reveal>
    </div>
  </section>
);
